import { useState, type ReactNode } from 'react'
import { Form, Modal, type FormInstance } from 'antd'
import { DEFAULT_CLIENT_TEXT_COLOR } from '@/features/client/clientFormShared'

type ReportFormValues = {
  primaryColor?: string
  secondaryColor?: string
  textColor?: string
}

type Colors = {
  primary: string
  secondary: string
  text: string
}

const DEFAULT_PRIMARY = '#1E40AF'
const DEFAULT_SECONDARY = '#647488'

// Fixed neutrals — the three brand colors above stay dynamic (driven by the form).
const HAIRLINE = '#DCE0E5'
const LABEL_GRAY = '#8A8A8A'
const BODY_GRAY = '#3F3F3F'
const CRITICAL_RED = '#9B1C1C'

// Editorial type system mirroring the generated PDF: a high-contrast Caslon serif for
// display, a grotesque sans for body/labels. Named faces first, web-safe fallbacks after.
const SERIF = "'Libre Caslon Text', Georgia, 'Times New Roman', Times, serif"
const SANS = "'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif"

const PAGE_W = 595.28
const PAGE_H = 841.89
// Page 1 (cover) uses a slightly wider left inset than page 2, matching the generated PDF.
const P1_L = 57
const P1_W = 481

const FALLBACK_ORG_NAME = 'Dashboard Insights'

// Sample period + year watermark shown on the cover (real reports derive these from the period).
const SAMPLE_PERIOD = 'JUL 2024–JUN 2025'
const SAMPLE_YEAR = '25'

// Illustrative placeholder content for the preview — not tied to any real report.
const SAMPLE_METRICS: Array<{ label: string; value: string; critical?: boolean }> = [
  { label: 'Revenue', value: '$4.24M (+8.3% YoY)' },
  { label: 'Gross Margin', value: '41.5% (up from 38.2%)' },
  { label: 'Operating Cash Flow', value: '$612K (+14% YoY)' },
  { label: 'Net Profit', value: '$487K (11.6% margin)' },
  { label: 'Cash Runway', value: '5.2 months (Watch)', critical: true },
  { label: 'Debtor Days', value: '42 days (down from 51)' },
  { label: 'Overhead Ratio', value: '31% of revenue' },
]

const SAMPLE_FINDINGS: string[] = [
  'Revenue grew 8.3% to $4.24M, led by the services line, which now contributes 46% of total income — up from 39% a year ago.',
  'Gross margin expanded 3.3 points to 41.5% on renegotiated supplier terms and a shift toward higher-value engagements, lifting profit without extra volume.',
  'Operating cash flow of $612K outpaced net profit, reflecting tighter receivables management and a nine-day fall in average debtor days.',
  'Cash runway sits at 5.2 months against current burn — workable, but thin enough to warrant a buffer before the next hiring round.',
  'Overheads climbed 12% year-on-year, ahead of revenue growth, with software subscriptions and contractor spend the largest contributors.',
]

const SAMPLE_RECOMMENDATIONS: Array<{ title: string; description: string }> = [
  {
    title: 'Build a Cash Buffer',
    description:
      'Set aside a six-month operating reserve before committing to new headcount, and adopt a 13-week rolling cash forecast to track runway weekly.',
  },
  {
    title: 'Formalise Pricing Reviews',
    description:
      'Recent margin gains show real pricing power. Introduce an annual pricing review and a value-based rate card for the services line to lock it in.',
  },
  {
    title: 'Rein In Overheads',
    description:
      'Audit software and contractor spend, consolidate overlapping tools, and hold overheads under 30% of revenue as the business scales.',
  },
  {
    title: 'Protect Recurring Revenue',
    description:
      'With services near half of income, invest in account management and retention so this higher-margin stream keeps compounding.',
  },
  {
    title: 'Sharpen Reporting Cadence',
    description:
      'Move to monthly management accounts with a one-page KPI dashboard so the board can act on trends well before year-end.',
  },
]

const twoDigit = (n: number) => String(n).padStart(2, '0')

// Split point for the two recommendation columns: left gets the first half (01–03), right the rest.
const recSplit = Math.ceil(SAMPLE_RECOMMENDATIONS.length / 2)

function ReportPage1({ colors, orgName }: { colors: Colors; orgName: string }) {
  return (
    <div
      style={{
        width: PAGE_W,
        height: PAGE_H,
        background: '#fff',
        fontFamily: SERIF,
        textAlign: 'left',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Full-bleed top bar */}
      <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: 8, background: colors.primary }} />

      {/* Header: subject company (left) + advisory period (right) over a hairline */}
      <div
        style={{
          position: 'absolute',
          left: P1_L,
          top: 50,
          width: P1_W,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 16,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 11, color: colors.text, whiteSpace: 'nowrap' }}>{orgName}</span>
        <span
          style={{
            fontFamily: SANS,
            fontSize: 7,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            color: colors.secondary,
            whiteSpace: 'nowrap',
          }}
        >
          Advisory · {SAMPLE_PERIOD}
        </span>
      </div>
      <div style={{ position: 'absolute', left: P1_L, top: 70, width: P1_W, height: 1, background: HAIRLINE }} />

      {/* Year watermark, right-aligned behind the title */}
      <div
        style={{
          position: 'absolute',
          right: P1_L,
          top: 337,
          fontSize: 150,
          fontWeight: 700,
          lineHeight: 1,
          color: colors.secondary,
          opacity: 0.15,
          userSelect: 'none',
        }}
      >
        {SAMPLE_YEAR}
      </div>

      {/* Title block — flowed so the tall Caslon metrics keep even line spacing and a clear
          gap above "Business" regardless of the loaded font. */}
      <div style={{ position: 'absolute', left: P1_L, top: 300, width: 470 }}>
        <div style={{ fontSize: 16, fontStyle: 'italic', lineHeight: 1, marginBottom: 10, color: colors.secondary }}>
          A financial health assessment
        </div>
        <div style={{ fontSize: 61, fontWeight: 700, lineHeight: 1, color: colors.primary }}>Business</div>
        <div style={{ fontSize: 61, fontWeight: 700, lineHeight: 1, color: colors.primary }}>Advisory</div>
        <div style={{ fontSize: 61, fontStyle: 'italic', lineHeight: 1, color: colors.primary }}>Report</div>
      </div>

      {/* Footer */}
      <div style={{ position: 'absolute', left: P1_L, top: 766, width: P1_W, height: 1, background: HAIRLINE }} />
      <div
        style={{
          position: 'absolute',
          left: P1_L,
          top: 773,
          fontFamily: SANS,
          fontSize: 7,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          color: LABEL_GRAY,
        }}
      >
        {SAMPLE_PERIOD}
      </div>
    </div>
  )
}

function ReportPage2({ colors, orgName }: { colors: Colors; orgName: string }) {
  return (
    <div
      style={{
        width: PAGE_W,
        height: PAGE_H,
        background: '#fff',
        fontFamily: SANS,
        textAlign: 'left',
        overflow: 'hidden',
        padding: '30px 50px 34px',
        boxSizing: 'border-box',
      }}
    >
      {/* Running header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 7, letterSpacing: 1.5, fontWeight: 700, color: colors.secondary, textTransform: 'uppercase' }}>
          {orgName} · Advisory Report
        </span>
        <span style={{ fontSize: 7, letterSpacing: 1.5, fontWeight: 700, color: LABEL_GRAY }}>01</span>
      </div>

      {/* Section title */}
      <div style={{ fontFamily: SERIF, fontSize: 34, lineHeight: 1.05, color: colors.primary, marginTop: 6 }}>
        Executive Summary
      </div>
      <div style={{ width: 172, height: 4, background: colors.secondary, marginTop: 8 }} />

      {/* By the Numbers + Findings */}
      <div style={{ display: 'grid', gridTemplateColumns: '175px 1fr', columnGap: 12, marginTop: 20, alignItems: 'start' }}>
        <div style={{ border: `1px solid ${colors.text}`, padding: '9px 10px' }}>
          <div style={{ fontFamily: SERIF, fontWeight: 700, fontSize: 12, color: colors.text }}>By the Numbers</div>
          <div style={{ height: 1, background: colors.text, marginTop: 6, marginBottom: 8 }} />
          {SAMPLE_METRICS.map((m, i) => (
            <div key={i} style={{ marginBottom: i === SAMPLE_METRICS.length - 1 ? 0 : 7 }}>
              <div style={{ fontSize: 7, letterSpacing: 0.5, textTransform: 'uppercase', color: LABEL_GRAY }}>
                {m.label}
              </div>
              <div
                style={{
                  fontFamily: SERIF,
                  fontWeight: 700,
                  fontSize: 10,
                  lineHeight: 1.25,
                  color: m.critical ? CRITICAL_RED : colors.text,
                }}
              >
                {m.value}
              </div>
            </div>
          ))}
        </div>

        <div>
          <div style={{ fontSize: 7, letterSpacing: 2, fontWeight: 700, color: colors.secondary }}>FINDINGS</div>
          {SAMPLE_FINDINGS.map((f, i) => (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: '29px 1fr',
                alignItems: 'start',
                paddingTop: 8,
                marginTop: 8,
                borderTop: `1px solid ${HAIRLINE}`,
              }}
            >
              <span style={{ fontFamily: SERIF, fontWeight: 700, fontSize: 18, color: colors.secondary, lineHeight: 1 }}>
                {twoDigit(i + 1)}
              </span>
              <span style={{ fontSize: 9, lineHeight: 1.55, color: BODY_GRAY }}>{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendations: fill the left column first (01–03), then the right (04–05). */}
      <div style={{ marginTop: 22 }}>
        <div style={{ fontSize: 7, letterSpacing: 2, fontWeight: 700, color: colors.secondary, marginBottom: 12 }}>
          RECOMMENDATIONS
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 26, alignItems: 'start' }}>
          {[
            SAMPLE_RECOMMENDATIONS.slice(0, recSplit),
            SAMPLE_RECOMMENDATIONS.slice(recSplit),
          ].map((column, columnIndex) => (
            <div key={columnIndex}>
              {column.map((r, rowIndex) => {
                const number = (columnIndex === 0 ? rowIndex : recSplit + rowIndex) + 1
                return (
                  <div
                    key={r.title}
                    style={{
                      breakInside: 'avoid',
                      borderTop: `1px solid ${HAIRLINE}`,
                      paddingTop: 8,
                      marginBottom: 14,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: SERIF,
                        fontWeight: 700,
                        fontSize: 11,
                        lineHeight: 1.2,
                        textTransform: 'uppercase',
                        color: colors.primary,
                      }}
                    >
                      <span style={{ color: colors.secondary }}>{twoDigit(number)}</span> {r.title}
                    </div>
                    <div style={{ fontSize: 9, lineHeight: 1.5, color: BODY_GRAY, marginTop: 5 }}>{r.description}</div>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function ScaledPage({ width, children }: { width: number; children: ReactNode }) {
  const scale = width / PAGE_W
  const height = PAGE_H * scale
  return (
    <div
      style={{
        width,
        height,
        overflow: 'hidden',
        background: '#fff',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
        border: '1px solid #E5E7EB',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: PAGE_W,
          height: PAGE_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  )
}

export function PdfReportPreview({
  form,
  orgName,
}: {
  form: FormInstance<ReportFormValues>
  // Accepted for API compatibility with the settings panel; the new cover/summary
  // pages surface only the analyzed company (orgName), matching the generated PDF.
  clientName?: string
  orgName?: string
}) {
  const [open, setOpen] = useState(false)
  const primary = (Form.useWatch('primaryColor', form) || '').trim() || DEFAULT_PRIMARY
  const secondary = (Form.useWatch('secondaryColor', form) || '').trim() || DEFAULT_SECONDARY
  const text = (Form.useWatch('textColor', form) || '').trim() || DEFAULT_CLIENT_TEXT_COLOR
  const colors: Colors = { primary, secondary, text }
  const resolvedOrgName = orgName?.trim() || FALLBACK_ORG_NAME

  return (
    <div className="mt-6">
      <div className="text-sm font-medium text-gray-700 mb-2">PDF preview</div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex flex-wrap gap-4 p-3 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer text-left"
        aria-label="Open enlarged PDF preview"
      >
        <ScaledPage width={200}>
          <ReportPage1 colors={colors} orgName={resolvedOrgName} />
        </ScaledPage>
        <ScaledPage width={200}>
          <ReportPage2 colors={colors} orgName={resolvedOrgName} />
        </ScaledPage>
      </button>
      <div className="text-xs text-gray-500 mt-1.5">Click to enlarge · Pages 1 and 2</div>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={1000}
        title="PDF preview"
        centered
        destroyOnClose
      >
        <div style={{ display: 'flex', gap: 18, justifyContent: 'center', padding: '8px 0 4px' }}>
          <ScaledPage width={460}>
            <ReportPage1 colors={colors} orgName={resolvedOrgName} />
          </ScaledPage>
          <ScaledPage width={460}>
            <ReportPage2 colors={colors} orgName={resolvedOrgName} />
          </ScaledPage>
        </div>
      </Modal>
    </div>
  )
}
